import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'accent' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  children: ReactNode;
}

const VARIANT_STYLES: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--gc-green-dark)',
    color: 'var(--gc-white)',
    border: '1.5px solid var(--gc-green-dark)',
  },
  secondary: {
    background: 'var(--gc-white)',
    color: 'var(--gc-green-dark)',
    border: '1.5px solid var(--gc-green-dark)',
  },
  tertiary: {
    background: 'transparent',
    color: 'var(--gc-green-dark)',
    border: '1.5px solid transparent',
    padding: '10px 4px',
  },
  accent: {
    background: 'var(--gc-orange)',
    color: 'var(--gc-ink)',
    border: '1.5px solid var(--gc-orange)',
  },
  danger: {
    background: 'var(--gc-white)',
    color: '#82261F',
    border: '1.5px solid var(--gc-danger)',
  },
};

const SIZE_STYLES: Record<ButtonSize, CSSProperties> = {
  sm: { padding: '8px 16px', fontSize: 11 },
  md: { padding: '11px 22px', fontSize: 12 },
  lg: { padding: '14px 28px', fontSize: 13 },
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  style,
  ...rest
}: ButtonProps) {
  return (
    <button
      data-variant={variant}
      data-size={size}
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        fontFamily: 'var(--font-sans)',
        fontWeight: 'var(--weight-semibold)' as unknown as number,
        letterSpacing: 'var(--track-wide)',
        textTransform: 'uppercase',
        borderRadius: 'var(--radius-pill)',
        cursor: 'pointer',
        transition: 'all var(--dur-fast) var(--ease-out)',
        whiteSpace: 'nowrap',
        ...VARIANT_STYLES[variant],
        ...SIZE_STYLES[size],
        ...style,
      }}
    >
      {icon ? <span style={{ display: 'inline-flex' }}>{icon}</span> : null}
      {children}
    </button>
  );
}
